import type { FC, FormEvent, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import type {
    BookingErrorResponse,
    BookingSelection,
    BookingSuccessResponse,
    DayAvailability,
    PatientType,
    ServiceId,
    SessionId
} from '@/types'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { isBookable } from '@/lib/availability'
import { Button, buttonVariants } from '@/components/ui/button'
import { SlotStatusBadge } from '@/components/landing/slot-status'

import {
    ArrowLeft,
    ArrowRight,
    CalendarCheck,
    CalendarDays,
    Check,
    CircleAlert,
    Copy,
    CopyCheck,
    LoaderCircle,
    MessageCircle,
    Sun,
    Sunset
} from 'lucide-react'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle
} from '@/components/ui/dialog'

import {
    formatDayLong,
    MONTH_SHORT,
    parseIsoDate,
    WEEKDAY_SHORT
} from '@/lib/dates'

import {
    COMPLAINT_MAX,
    NAME_MAX,
    validateName,
    validatePhone
} from '@/lib/booking-validation'

import {
    clinic,
    serviceOrder,
    services,
    sessionOrder,
    sessions,
    whatsappLink
} from '@/constants/clinic'

type Step = 1 | 2 | 3 | 'success'

type FieldName = 'name' | 'phone' | 'complaint' | 'consent'

type FieldErrors = Partial<Record<FieldName, string>>

const steps: Array<{ id: 1 | 2 | 3, label: string }> = [
    { id: 1, label: 'Layanan' },
    { id: 2, label: 'Jadwal' },
    { id: 3, label: 'Data pasien' }
]

const stepCopy: Record<1 | 2 | 3, { title: string, description: string }> = {
    1: {
        title: 'Pilih layanan',
        description: 'Janji online tersedia untuk poli rawat jalan. Untuk keadaan darurat, langsung datang ke UGD.'
    },
    2: {
        title: 'Pilih jadwal kunjungan',
        description: 'Kuota diperbarui otomatis. Janji online bisa dibuat untuk 6 hari kerja ke depan.'
    },
    3: {
        title: 'Data pasien',
        description: 'Isi data pasien yang akan diperiksa. Tidak perlu membuat akun.'
    }
}

const sessionIcons: Record<SessionId, LucideIcon> = {
    pagi: Sun,
    sore: Sunset
}

const inputClass = (invalid?: boolean): string => {
    return cn(
        'block w-full rounded-xl border bg-white px-4 py-3 text-base text-ink shadow-xs outline-none transition-[border-color,box-shadow] placeholder:text-slate-400 focus:ring-4 sm:text-[15px]',
        invalid
            ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500/15'
            : 'border-input focus:border-brand-500 focus:ring-brand-500/15'
    )
}

const hasOpenSession = (day: DayAvailability | undefined, service: ServiceId): boolean => {
    if (!day) return false

    return sessionOrder.some(session => isBookable(day.slots[service][session]))
}

const resolveInitialDate = (days: DayAvailability[], initial: BookingSelection): string | undefined => {
    const service = initial.service

    if (!service) return undefined

    const requested = days.find(day => day.date === initial.date)

    if (requested && hasOpenSession(requested, service)) return requested.date

    return days.find(day => hasOpenSession(day, service))?.date
}

const resolveInitialSession = (days: DayAvailability[], initial: BookingSelection): SessionId | undefined => {
    const service = initial.service
    const date = resolveInitialDate(days, initial)

    if (!service || !date || !initial.session || date !== initial.date) return undefined

    const day = days.find(item => item.date === date)

    return isBookable(day?.slots[service][initial.session]) ? initial.session : undefined
}

interface FieldProps {
    id: string
    label: string
    hint?: string
    error?: string
    optional?: boolean
    children: ReactNode
}

const Field: FC<FieldProps> = ({
    id,
    label,
    hint,
    error,
    optional,
    children
}): ReactNode => {
    return (
        <div>
            <label htmlFor={id} className='flex items-baseline justify-between gap-2 text-sm font-semibold text-ink'>
                {label}

                {optional && (
                    <span className='text-xs font-medium text-muted-foreground'>
                        Opsional
                    </span>
                )}
            </label>

            <div className='mt-2'>
                {children}
            </div>

            {error ? (
                <p id={`${id}-error`} className='mt-1.5 flex items-start gap-1.5 text-sm text-rose-600'>
                    <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    {error}
                </p>
            ) : hint ? (
                <p id={`${id}-hint`} className='mt-1.5 text-xs text-muted-foreground'>
                    {hint}
                </p>
            ) : null}
        </div>
    )
}

const Stepper: FC<{ current: 1 | 2 | 3 }> = ({ current }): ReactNode => {
    return (
        <ol className='mt-5 flex items-center gap-2' aria-label='Langkah janji online'>
            {steps.map((item, index) => {
                const state = item.id < current ? 'done' : item.id === current ? 'current' : 'upcoming'

                return (
                    <li
                        key={item.id}
                        className='flex flex-1 items-center gap-2'
                        aria-current={state === 'current' ? 'step' : undefined}
                    >
                        <span
                            className={cn(
                                'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                                state === 'upcoming' ? 'bg-muted text-muted-foreground' : 'bg-brand-600 text-white',
                                state === 'current' && 'ring-4 ring-brand-100'
                            )}
                        >
                            {state === 'done' ? <Check className='size-3.5' aria-hidden='true' /> : item.id}
                        </span>

                        <span className={cn('text-xs font-semibold whitespace-nowrap', state === 'upcoming' ? 'text-muted-foreground' : 'text-ink')}>
                            {item.label}
                        </span>

                        {index < steps.length - 1 && (
                            <span
                                className={cn('h-px min-w-3 flex-1', item.id < current ? 'bg-brand-400' : 'bg-border')}
                                aria-hidden='true'
                            />
                        )}
                    </li>
                )
            })}
        </ol>
    )
}

interface BookingDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    days: DayAvailability[]
    initial: BookingSelection
    onBooked: () => void | Promise<void>
}

const BookingDialog: FC<BookingDialogProps> = ({
    open,
    onOpenChange,
    days,
    initial,
    onBooked
}): ReactNode => {
    const [service, setService] = useState<ServiceId | undefined>(() => initial.service)
    const [date, setDate] = useState<string | undefined>(() => resolveInitialDate(days, initial))
    const [session, setSession] = useState<SessionId | undefined>(() => resolveInitialSession(days, initial))
    const [step, setStep] = useState<Step>(() => {
        if (!initial.service) return 1

        return resolveInitialSession(days, initial) ? 3 : 2
    })

    const [name, setName] = useState('')
    const [phone, setPhone] = useState('')
    const [patientType, setPatientType] = useState<PatientType>('umum')
    const [complaint, setComplaint] = useState('')
    const [consent, setConsent] = useState(false)
    const [website, setWebsite] = useState('')

    const [errors, setErrors] = useState<FieldErrors>({})
    const [scheduleError, setScheduleError] = useState<string | null>(null)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [code, setCode] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)

    const popupRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        popupRef.current?.scrollTo({ top: 0 })
    }, [step])

    const findDay = (iso?: string): DayAvailability | undefined => days.find(day => day.date === iso)

    const selectedDay = findDay(date)
    const selectedSlot = service && selectedDay && session ? selectedDay.slots[service][session] : undefined
    const canContinue = Boolean(service && selectedDay && session && isBookable(selectedSlot))
    const serviceHasOpenDays = service ? days.some(day => hasOpenSession(day, service)) : false

    const chooseService = (id: ServiceId): void => {
        const keepDate = date && hasOpenSession(findDay(date), id) ? date : undefined
        const nextDate = keepDate ?? days.find(day => hasOpenSession(day, id))?.date
        const nextDay = findDay(nextDate)

        setService(id)
        setDate(nextDate)
        setSession(current => (current && nextDay && isBookable(nextDay.slots[id][current]) ? current : undefined))
        setScheduleError(null)
        setStep(2)
    }

    const chooseDate = (iso: string): void => {
        const day = findDay(iso)

        setDate(iso)
        setScheduleError(null)

        if (service && session && !isBookable(day?.slots[service][session])) setSession(undefined)
    }

    const focusField = (field: FieldName): void => {
        const element = document.getElementById(`booking-${field}`)

        element?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (!service || !date || !session) {
            setStep(2)
            return
        }

        const nextErrors: FieldErrors = {}
        const nameError = validateName(name)
        const phoneError = validatePhone(phone)

        if (nameError) nextErrors.name = nameError
        if (phoneError) nextErrors.phone = phoneError
        if (complaint.trim().length > COMPLAINT_MAX) nextErrors.complaint = `Keluhan maksimal ${COMPLAINT_MAX} karakter.`
        if (!consent) nextErrors.consent = 'Centang persetujuan ini untuk mengirim permintaan janji.'

        setErrors(nextErrors)
        setSubmitError(null)

        const firstInvalid = (['name', 'phone', 'complaint', 'consent'] as FieldName[]).find(field => nextErrors[field])

        if (firstInvalid) {
            focusField(firstInvalid)
            return
        }

        setSubmitting(true)

        try {
            const response = await fetch('/api/janji', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    service,
                    date,
                    session,
                    name,
                    phone,
                    patientType,
                    complaint,
                    website
                })
            })

            const data = await response.json() as BookingSuccessResponse | BookingErrorResponse

            if (data.ok) {
                setCode(data.code)
                setStep('success')
                void onBooked()
                return
            }

            if (data.field === 'name' || data.field === 'phone' || data.field === 'complaint') {
                setErrors({ [data.field]: data.message })
                focusField(data.field)
                return
            }

            if (data.field === 'service' || data.field === 'date' || data.field === 'session') {
                setSession(undefined)
                setScheduleError(data.message)
                setStep(2)
                void onBooked()
                return
            }

            setSubmitError(data.message)
        }
        catch {
            setSubmitError(`Koneksi terputus. Periksa internet Anda lalu coba lagi, atau hubungi klinik di ${clinic.phone.label}.`)
        }
        finally {
            setSubmitting(false)
        }
    }

    const copyCode = async (): Promise<void> => {
        if (!code) return

        try {
            await navigator.clipboard.writeText(code)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 2000)
        }
        catch {
            setCopied(false)
        }
    }

    const renderServiceStep = (): ReactNode => (
        <div className='grid gap-3'>
            {serviceOrder.map(id => {
                const item = services[id]
                const Icon = item.icon
                const openDays = days.filter(day => hasOpenSession(day, id)).length
                const selected = service === id

                return (
                    <button
                        key={id}
                        type='button'
                        onClick={() => chooseService(id)}
                        aria-pressed={selected}
                        className={cn(
                            'group flex w-full items-center gap-4 rounded-2xl border bg-white p-4 text-left transition-all outline-none hover:border-brand-300 hover:shadow-md hover:shadow-brand-900/5 focus-visible:ring-4 focus-visible:ring-ring/30',
                            selected ? 'border-brand-500 bg-brand-50/60 ring-1 ring-brand-500' : 'border-border'
                        )}
                    >
                        <span className='flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-brand-100'>
                            <Icon className='size-6' aria-hidden='true' />
                        </span>

                        <span className='min-w-0 flex-1'>
                            <span className='block font-semibold text-ink'>
                                {item.name}
                            </span>

                            <span className='mt-0.5 block text-sm text-muted-foreground'>
                                {item.short}
                            </span>

                            <span className={cn('mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold', openDays > 0 ? 'text-emerald-700' : 'text-rose-600')}>
                                <span className={cn('size-1.5 rounded-full', openDays > 0 ? 'bg-emerald-500' : 'bg-rose-500')} aria-hidden='true' />
                                {openDays > 0 ? `Kuota tersedia di ${openDays} hari` : 'Kuota penuh 6 hari ke depan'}
                            </span>
                        </span>

                        <ArrowRight className='size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-brand-600' aria-hidden='true' />
                    </button>
                )
            })}
        </div>
    )

    const renderScheduleStep = (): ReactNode => (
        <div className='grid gap-6'>
            {scheduleError && (
                <div role='alert' className='flex items-start gap-3 rounded-2xl bg-rose-50 p-4 text-sm text-rose-800 ring-1 ring-rose-200 ring-inset'>
                    <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    {scheduleError}
                </div>
            )}

            {service && (
                <div className='flex items-center justify-between gap-3 rounded-2xl bg-muted/70 px-4 py-3'>
                    <span className='text-sm text-muted-foreground'>
                        Layanan:
                        {' '}
                        <span className='font-semibold text-ink'>
                            {services[service].name}
                        </span>
                    </span>

                    <button
                        type='button'
                        onClick={() => setStep(1)}
                        className='rounded-full px-2 py-1 text-sm font-semibold text-brand-700 outline-none hover:underline focus-visible:ring-4 focus-visible:ring-ring/30'
                    >
                        Ganti
                    </button>
                </div>
            )}

            <fieldset>
                <legend className='text-sm font-semibold text-ink'>
                    Tanggal kunjungan
                </legend>

                <div className='mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6'>
                    {days.map(day => {
                        const parsed = parseIsoDate(day.date)
                        const enabled = service ? hasOpenSession(day, service) : false
                        const selected = day.date === date

                        if (!parsed) return null

                        return (
                            <button
                                key={day.date}
                                type='button'
                                disabled={!enabled}
                                aria-pressed={selected}
                                aria-label={`${formatDayLong(day.date)}${enabled ? '' : ', kuota penuh atau tidak praktik'}`}
                                onClick={() => chooseDate(day.date)}
                                className={cn(
                                    'flex flex-col items-center rounded-2xl border px-2 py-2.5 text-center transition-all outline-none focus-visible:ring-4 focus-visible:ring-ring/30',
                                    selected
                                        ? 'border-brand-600 bg-brand-600 text-white shadow-md shadow-brand-700/25'
                                        : 'border-border bg-white text-ink hover:border-brand-300',
                                    !enabled && 'cursor-not-allowed border-dashed bg-muted/40 text-muted-foreground opacity-70 hover:border-border'
                                )}
                            >
                                <span className='text-xs font-medium opacity-80'>
                                    {WEEKDAY_SHORT[parsed.getUTCDay()]}
                                </span>

                                <span className='text-lg leading-tight font-bold tabular-nums'>
                                    {parsed.getUTCDate()}
                                </span>

                                <span className='text-xs opacity-80'>
                                    {MONTH_SHORT[parsed.getUTCMonth()]}
                                </span>
                            </button>
                        )
                    })}
                </div>

                {service && !serviceHasOpenDays && (
                    <p className='mt-3 text-sm text-rose-700'>
                        Kuota
                        {' '}
                        {services[service].name}
                        {' '}
                        penuh untuk 6 hari kerja ke depan. Hubungi klinik di
                        {' '}
                        <a href={clinic.phone.href} className='font-semibold underline underline-offset-2'>
                            {clinic.phone.label}
                        </a>
                        .
                    </p>
                )}
            </fieldset>

            <fieldset disabled={!selectedDay}>
                <legend className='text-sm font-semibold text-ink'>
                    Sesi
                </legend>

                <div className='mt-3 grid gap-3 sm:grid-cols-2'>
                    {sessionOrder.map(id => {
                        const slot = service && selectedDay ? selectedDay.slots[service][id] : undefined
                        const enabled = isBookable(slot)
                        const selected = session === id
                        const Icon = sessionIcons[id]

                        return (
                            <button
                                key={id}
                                type='button'
                                disabled={!enabled}
                                aria-pressed={selected}
                                onClick={() => {
                                    setSession(id)
                                    setScheduleError(null)
                                }}
                                className={cn(
                                    'flex flex-col items-start gap-2.5 rounded-2xl border p-4 text-left transition-all outline-none focus-visible:ring-4 focus-visible:ring-ring/30',
                                    selected
                                        ? 'border-brand-500 bg-brand-50/60 ring-1 ring-brand-500'
                                        : 'border-border bg-white hover:border-brand-300',
                                    !enabled && 'cursor-not-allowed bg-muted/40 hover:border-border'
                                )}
                            >
                                <span className='flex w-full flex-wrap items-center justify-between gap-2'>
                                    <span className={cn('inline-flex items-center gap-2 font-semibold', enabled ? 'text-ink' : 'text-muted-foreground')}>
                                        <Icon className={cn('size-4', id === 'pagi' ? 'text-amber-500' : 'text-sky-600')} aria-hidden='true' />
                                        {sessions[id].name}
                                    </span>

                                    {slot && <SlotStatusBadge status={slot.status} />}
                                </span>

                                <span className='text-sm text-muted-foreground'>
                                    Pendaftaran
                                    {' '}
                                    <span className='tabular-nums'>
                                        {sessions[id].registration}
                                    </span>
                                    {' '}
                                    WIB
                                </span>

                                {slot?.status === 'hampir-penuh' && (
                                    <span className='text-xs font-semibold text-amber-800'>
                                        Sisa
                                        {' '}
                                        {slot.remaining}
                                        {' '}
                                        kuota
                                    </span>
                                )}
                            </button>
                        )
                    })}
                </div>
            </fieldset>
        </div>
    )

    const renderPatientStep = (): ReactNode => {
        if (!service || !date || !session) return null

        const ServiceIcon = services[service].icon
        const SessionIcon = sessionIcons[session]

        return (
            <div className='grid gap-5'>
                <div className='flex items-start gap-3 rounded-2xl bg-brand-50/70 p-4 ring-1 ring-brand-100 ring-inset'>
                    <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-white text-brand-700 shadow-xs'>
                        <ServiceIcon className='size-5' aria-hidden='true' />
                    </span>

                    <div className='min-w-0 flex-1 text-sm'>
                        <p className='font-semibold text-ink'>
                            {services[service].name}
                        </p>

                        <p className='mt-0.5 text-muted-foreground'>
                            {formatDayLong(date)}
                        </p>

                        <p className='mt-0.5 inline-flex items-center gap-1.5 text-muted-foreground'>
                            <SessionIcon className='size-3.5' aria-hidden='true' />
                            {sessions[session].name}
                            , pendaftaran
                            {' '}
                            <span className='tabular-nums'>
                                {sessions[session].registration}
                            </span>
                            {' '}
                            WIB
                        </p>
                    </div>

                    <button
                        type='button'
                        onClick={() => setStep(2)}
                        className='shrink-0 rounded-full px-2 py-1 text-sm font-semibold text-brand-700 outline-none hover:underline focus-visible:ring-4 focus-visible:ring-ring/30'
                    >
                        Ubah
                    </button>
                </div>

                <Field id='booking-name' label='Nama lengkap pasien' error={errors.name}>
                    <input
                        id='booking-name'
                        name='name'
                        autoComplete='name'
                        maxLength={NAME_MAX}
                        value={name}
                        onChange={event => {
                            setName(event.target.value)
                            if (errors.name) setErrors(current => ({ ...current, name: undefined }))
                        }}
                        placeholder='Sesuai KTP atau kartu JKN'
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? 'booking-name-error' : undefined}
                        className={inputClass(Boolean(errors.name))}
                    />
                </Field>

                <Field
                    id='booking-phone'
                    label='Nomor WhatsApp'
                    hint='Petugas klinik mengonfirmasi janji lewat nomor ini.'
                    error={errors.phone}
                >
                    <input
                        id='booking-phone'
                        name='phone'
                        type='tel'
                        inputMode='tel'
                        autoComplete='tel'
                        value={phone}
                        onChange={event => {
                            setPhone(event.target.value)
                            if (errors.phone) setErrors(current => ({ ...current, phone: undefined }))
                        }}
                        placeholder='0812 3456 7890'
                        aria-invalid={Boolean(errors.phone)}
                        aria-describedby={errors.phone ? 'booking-phone-error' : 'booking-phone-hint'}
                        className={inputClass(Boolean(errors.phone))}
                    />
                </Field>

                <fieldset>
                    <legend className='text-sm font-semibold text-ink'>
                        Jenis pasien
                    </legend>

                    <div className='mt-2 grid grid-cols-2 gap-2'>
                        {([
                            { value: 'umum', label: 'Pasien umum' },
                            { value: 'jkn', label: 'Peserta JKN (BPJS)' }
                        ] as Array<{ value: PatientType, label: string }>).map(option => (
                            <label
                                key={option.value}
                                className='flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-input bg-white px-3 py-3 text-center text-sm font-semibold text-ink transition-all has-checked:border-brand-500 has-checked:bg-brand-50 has-checked:text-brand-800 has-checked:ring-1 has-checked:ring-brand-500 has-focus-visible:ring-4 has-focus-visible:ring-ring/30'
                            >
                                <input
                                    type='radio'
                                    name='patientType'
                                    value={option.value}
                                    checked={patientType === option.value}
                                    onChange={() => setPatientType(option.value)}
                                    className='sr-only'
                                />
                                {option.label}
                            </label>
                        ))}
                    </div>
                </fieldset>

                <Field
                    id='booking-complaint'
                    label='Keluhan singkat'
                    optional
                    hint={`${complaint.length}/${COMPLAINT_MAX} karakter`}
                    error={errors.complaint}
                >
                    <textarea
                        id='booking-complaint'
                        name='complaint'
                        rows={3}
                        maxLength={COMPLAINT_MAX}
                        value={complaint}
                        onChange={event => setComplaint(event.target.value)}
                        placeholder='Contoh: demam sejak dua hari, batuk, dan pusing.'
                        aria-invalid={Boolean(errors.complaint)}
                        aria-describedby={errors.complaint ? 'booking-complaint-error' : 'booking-complaint-hint'}
                        className={cn(inputClass(Boolean(errors.complaint)), 'resize-none')}
                    />
                </Field>

                <div>
                    <label className='flex cursor-pointer items-start gap-3 text-sm text-ink-soft'>
                        <input
                            id='booking-consent'
                            type='checkbox'
                            checked={consent}
                            onChange={event => {
                                setConsent(event.target.checked)
                                if (errors.consent) setErrors(current => ({ ...current, consent: undefined }))
                            }}
                            aria-invalid={Boolean(errors.consent)}
                            aria-describedby={errors.consent ? 'booking-consent-error' : undefined}
                            className='mt-0.5 size-4 shrink-0 accent-brand-600'
                        />
                        Data di atas benar, dan klinik boleh menghubungi saya lewat WhatsApp terkait janji ini.
                    </label>

                    {errors.consent && (
                        <p id='booking-consent-error' className='mt-1.5 flex items-start gap-1.5 text-sm text-rose-600'>
                            <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                            {errors.consent}
                        </p>
                    )}
                </div>

                <div className='hidden' aria-hidden='true'>
                    <label htmlFor='booking-website'>
                        Jangan isi kolom ini
                    </label>

                    <input
                        id='booking-website'
                        name='website'
                        tabIndex={-1}
                        autoComplete='off'
                        value={website}
                        onChange={event => setWebsite(event.target.value)}
                    />
                </div>

                {submitError && (
                    <div role='alert' className='flex items-start gap-3 rounded-2xl bg-rose-50 p-4 text-sm text-rose-800 ring-1 ring-rose-200 ring-inset'>
                        <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                        {submitError}
                    </div>
                )}
            </div>
        )
    }

    const renderSuccess = (): ReactNode => {
        if (!service || !date || !session || !code) return null

        const message = `Halo Klinik Sehat Budi Luhur, saya sudah mengirim permintaan janji ${code} untuk ${services[service].name} pada ${formatDayLong(date)}, ${sessions[session].name.toLowerCase()}.`

        return (
            <div className='px-5 py-8 text-center sm:px-8 sm:py-10'>
                <div className='mx-auto flex size-16 items-center justify-center rounded-full bg-emerald-50 ring-8 ring-emerald-50/60'>
                    <CalendarCheck className='size-8 text-emerald-600' aria-hidden='true' />
                </div>

                <DialogTitle className='mt-5 text-2xl'>
                    Permintaan janji terkirim
                </DialogTitle>

                <DialogDescription className='mx-auto mt-2 max-w-sm'>
                    Petugas klinik akan mengonfirmasi janji Anda lewat WhatsApp. Simpan kode ini dan tunjukkan saat mendaftar.
                </DialogDescription>

                <div className='mt-6 rounded-2xl border border-dashed border-brand-300 bg-brand-50/60 px-4 py-4'>
                    <p className='text-xs font-semibold tracking-wider text-brand-700 uppercase'>
                        Kode janji
                    </p>

                    <div className='mt-1 flex items-center justify-center gap-2'>
                        <span className='font-mono text-2xl font-bold tracking-wider text-ink'>
                            {code}
                        </span>

                        <button
                            type='button'
                            onClick={() => void copyCode()}
                            className='inline-flex size-9 items-center justify-center rounded-full text-brand-700 transition-colors outline-none hover:bg-white focus-visible:ring-4 focus-visible:ring-ring/30'
                        >
                            {copied ? <CopyCheck className='size-4' aria-hidden='true' /> : <Copy className='size-4' aria-hidden='true' />}

                            <span className='sr-only'>
                                {copied ? 'Kode tersalin' : 'Salin kode janji'}
                            </span>
                        </button>
                    </div>
                </div>

                <dl className='mt-4 grid gap-2 rounded-2xl bg-muted/60 p-4 text-left text-sm'>
                    <div className='flex justify-between gap-4'>
                        <dt className='text-muted-foreground'>
                            Layanan
                        </dt>

                        <dd className='text-right font-semibold text-ink'>
                            {services[service].name}
                        </dd>
                    </div>

                    <div className='flex justify-between gap-4'>
                        <dt className='text-muted-foreground'>
                            Tanggal
                        </dt>

                        <dd className='text-right font-semibold text-ink'>
                            {formatDayLong(date)}
                        </dd>
                    </div>

                    <div className='flex justify-between gap-4'>
                        <dt className='text-muted-foreground'>
                            Sesi
                        </dt>

                        <dd className='text-right font-semibold text-ink'>
                            {sessions[session].name}
                            , daftar
                            {' '}
                            <span className='tabular-nums'>
                                {sessions[session].registration}
                            </span>
                        </dd>
                    </div>
                </dl>

                <ul className='mt-4 grid gap-2 text-left text-sm text-ink-soft'>
                    <li className='flex items-start gap-2'>
                        <Check className='mt-0.5 size-4 shrink-0 text-brand-600' aria-hidden='true' />
                        Bawa KTP, dan kartu JKN (BPJS) bila Anda peserta JKN.
                    </li>

                    <li className='flex items-start gap-2'>
                        <Check className='mt-0.5 size-4 shrink-0 text-brand-600' aria-hidden='true' />
                        Datang pada jam pendaftaran sesi yang Anda pilih.
                    </li>
                </ul>

                <div className='mt-7 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center'>
                    <Button variant='outline' onClick={() => onOpenChange(false)}>
                        Selesai
                    </Button>

                    <a
                        href={whatsappLink(message)}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={buttonVariants()}
                    >
                        <MessageCircle className='size-4' aria-hidden='true' />
                        Kirim kode lewat WhatsApp
                    </a>
                </div>
            </div>
        )
    }

    const activeStep = step === 'success' ? null : step

    return (
        <Dialog open={open} onOpenChange={next => onOpenChange(next)}>
            <DialogContent
                ref={popupRef}
                className='max-h-[calc(100dvh-1.5rem)] max-w-xl gap-0 overflow-y-auto p-0 sm:p-0'
            >
                {activeStep === null ? renderSuccess() : (
                    <form onSubmit={handleSubmit} noValidate className='flex min-h-0 flex-col'>
                        <div className='border-b border-border px-5 pt-5 pb-5 sm:px-7 sm:pt-7'>
                            <p className='inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider text-brand-700 uppercase'>
                                <CalendarDays className='size-3.5' aria-hidden='true' />
                                Janji online
                            </p>

                            <DialogTitle className='mt-2 pr-10 text-xl sm:text-2xl'>
                                {stepCopy[activeStep].title}
                            </DialogTitle>

                            <DialogDescription className='mt-1.5'>
                                {stepCopy[activeStep].description}
                            </DialogDescription>

                            <Stepper current={activeStep} />
                        </div>

                        <div className='px-5 py-5 sm:px-7 sm:py-6'>
                            {activeStep === 1 && renderServiceStep()}
                            {activeStep === 2 && renderScheduleStep()}
                            {activeStep === 3 && renderPatientStep()}
                        </div>

                        {activeStep !== 1 && (
                            <div className='sticky bottom-0 flex items-center justify-between gap-3 border-t border-border bg-white/95 px-5 py-4 backdrop-blur sm:px-7'>
                                <Button
                                    variant='ghost'
                                    onClick={() => setStep(activeStep === 3 ? 2 : 1)}
                                    disabled={submitting}
                                >
                                    <ArrowLeft className='size-4' aria-hidden='true' />
                                    Kembali
                                </Button>

                                {activeStep === 2 ? (
                                    <Button onClick={() => setStep(3)} disabled={!canContinue}>
                                        Lanjut isi data
                                        <ArrowRight className='size-4' aria-hidden='true' />
                                    </Button>
                                ) : (
                                    <Button type='submit' disabled={submitting}>
                                        {submitting ? (
                                            <>
                                                <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                                                Mengirim...
                                            </>
                                        ) : (
                                            <>
                                                Kirim permintaan
                                                <ArrowRight className='size-4' aria-hidden='true' />
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        )}
                    </form>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default BookingDialog
